import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { EnvelopeSimple, Plus, Trash, PencilSimple, Check, X } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-gray-100 text-gray-600",
  active: "bg-green-100 text-green-700",
  reauth_required: "bg-red-100 text-red-700",
  inactive: "bg-amber-100 text-amber-700",
};

export const SharedInboxesCard = ({ members }) => {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [selMembers, setSelMembers] = useState([]);
  const [manageId, setManageId] = useState(null);
  const [manageSel, setManageSel] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(() => api.get("/shared-inboxes").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "shared_inbox") {
      toast.success(t("si.connected", "Shared inbox connected! Importing its mailbox — this takes a few seconds…"));
      window.history.replaceState({}, "", "/team");
      load();
    } else if (params.get("connect_error")) {
      const err = params.get("connect_error");
      toast.error(err === "email_already_connected"
        ? t("si.dup_email", "This Gmail address is already connected elsewhere. Disconnect it first.")
        : t("si.connect_failed", "Connection failed: {err} — please try again", { err }));
      window.history.replaceState({}, "", "/team");
    }
  }, [load]);

  const connect = async (sid) => {
    try {
      const { data: r } = await api.get(`/oauth/gmail/start?mailbox=${sid}`);
      window.location.href = r.url;
    } catch (err) { toast.error(errDetail(err)); }
  };

  if (!data) return null;
  const { shared_inboxes, used, limit, is_team_admin } = data;
  if (!is_team_admin && shared_inboxes.length === 0) return null;

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/shared-inboxes", { name, member_ids: selMembers });
      toast.success(t("si.created", "Shared inbox created"));
      setName(""); setSelMembers([]); load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const saveMembers = async (sid) => {
    try {
      await api.put(`/shared-inboxes/${sid}/members`, { member_ids: manageSel });
      toast.success(t("si.members_updated", "Member access updated"));
      setManageId(null); load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const rename = async (sid) => {
    try {
      await api.put(`/shared-inboxes/${sid}`, { name: renameVal });
      toast.success(t("si.renamed", "Renamed"));
      setRenamingId(null); load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/shared-inboxes/${deleteTarget.id}`, { data: { confirm: confirmText } });
      toast.success(t("si.deleted", "Shared inbox deleted — all its data was erased"));
      setDeleteTarget(null); setConfirmText(""); load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const statusOf = (s) => (!s.active ? "inactive" : s.status);
  const statusLabel = (s) => {
    const st = statusOf(s);
    if (st === "inactive") return t("si.status_inactive", "inactive — upgrade to reactivate");
    if (st === "pending") return t("si.status_pending", "not connected yet");
    if (st === "reauth_required") return t("si.status_reauth", "reconnect needed");
    return t("si.status_active", "active");
  };

  return (
    <div data-testid="shared-inboxes-card" className="border border-gray-200 rounded-md p-5 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-gray-900 flex items-center gap-2">
          <EnvelopeSimple size={17} className="text-blue-600" /> {t("si.title", "Shared Inboxes")}
        </h2>
        <span data-testid="si-limit-count" className="text-xs text-gray-500">
          {t("si.used_of", "{used} of {limit} used", { used, limit })}
        </span>
      </div>

      {shared_inboxes.length === 0 && (
        <p data-testid="si-empty" className="text-sm text-gray-400 mb-4">
          {t("si.empty", "No shared inboxes yet. Create one your whole team can work from (e.g. support@, sales@).")}
        </p>
      )}

      <div className="space-y-2 mb-4">
        {shared_inboxes.map((s) => (
          <div key={s.id} data-testid={`si-row-${s.id}`} className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color || "#2563eb" }} />
              {renamingId === s.id ? (
                <span className="flex items-center gap-1">
                  <input data-testid={`si-rename-input-${s.id}`} value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm" />
                  <button data-testid={`si-rename-save-${s.id}`} onClick={() => rename(s.id)} className="text-blue-600 p-1"><Check size={15} /></button>
                  <button onClick={() => setRenamingId(null)} className="text-gray-400 p-1"><X size={15} /></button>
                </span>
              ) : (
                <span data-testid={`si-name-${s.id}`} className="font-medium text-sm text-gray-900">{s.name}</span>
              )}
              <span className="text-xs text-gray-400">{s.email || t("si.no_email", "no mailbox connected")}</span>
              <span data-testid={`si-status-${s.id}`} className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${STATUS_STYLES[statusOf(s)] || STATUS_STYLES.pending}`}>
                {statusLabel(s)}
              </span>
              {is_team_admin && (
                <span className="ml-auto flex items-center gap-1">
                  {s.active && (s.status === "pending" || s.status === "reauth_required") && (
                    <button data-testid={`si-connect-btn-${s.id}`} onClick={() => connect(s.id)}
                      className={`text-xs font-semibold rounded-md px-3 py-1.5 text-white ${s.status === "reauth_required" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}>
                      {s.status === "reauth_required" ? t("si.reconnect", "Reconnect Gmail") : t("si.connect", "Connect Gmail")}
                    </button>
                  )}
                  <button data-testid={`si-rename-btn-${s.id}`} onClick={() => { setRenamingId(s.id); setRenameVal(s.name); }}
                    className="text-gray-400 hover:text-blue-600 p-1" title={t("si.rename", "Rename")}><PencilSimple size={15} /></button>
                  <button data-testid={`si-manage-btn-${s.id}`} onClick={() => { setManageId(manageId === s.id ? null : s.id); setManageSel(s.member_ids || []); }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 px-2 py-1">{t("si.manage", "Manage members")}</button>
                  <button data-testid={`si-delete-btn-${s.id}`} onClick={() => { setDeleteTarget(s); setConfirmText(""); }}
                    className="text-gray-400 hover:text-red-600 p-1" title={t("si.delete", "Delete")}><Trash size={15} /></button>
                </span>
              )}
            </div>
            <div className="mt-1.5 text-xs text-gray-500" data-testid={`si-members-${s.id}`}>
              {t("si.members_label", "Access:")} {(s.member_names || []).length ? s.member_names.join(", ") : t("si.no_members", "no members yet")}
            </div>
            {manageId === s.id && (
              <div data-testid={`si-manage-panel-${s.id}`} className="mt-3 border-t border-gray-100 pt-3">
                <div className="flex flex-wrap gap-3 mb-2">
                  {members.map((m) => (
                    <label key={m.id} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input data-testid={`si-member-toggle-${m.id}`} type="checkbox" checked={manageSel.includes(m.id)}
                        onChange={() => toggle(manageSel, setManageSel, m.id)} className="w-4 h-4 accent-blue-600" />
                      {m.name || m.email}
                    </label>
                  ))}
                </div>
                <button data-testid={`si-members-save-${s.id}`} onClick={() => saveMembers(s.id)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md px-3 py-1.5">
                  {t("si.save_access", "Save access")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {deleteTarget && (
        <div data-testid="si-delete-dialog" className="border border-red-200 bg-red-50 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800 mb-2">
            {t("si.delete_warning", "This permanently erases ALL data of \"{name}\" (emails, rules, memory, contacts, calendar). Type the inbox name to confirm.", { name: deleteTarget.name })}
          </p>
          <div className="flex gap-2">
            <input data-testid="si-delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder={deleteTarget.name} className="flex-1 border border-red-300 rounded-md px-3 py-1.5 text-sm" />
            <button data-testid="si-delete-confirm-btn" onClick={doDelete}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md px-3">{t("si.delete_confirm", "Delete forever")}</button>
            <button data-testid="si-delete-cancel-btn" onClick={() => setDeleteTarget(null)}
              className="text-xs text-gray-500 px-2">{t("common.cancel", "Cancel")}</button>
          </div>
        </div>
      )}

      {is_team_admin && (used < limit ? (
        <form onSubmit={create} className="border border-dashed border-gray-300 rounded-md p-4">
          <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1"><Plus size={13} /> {t("si.new", "New shared inbox")}</p>
          <div className="flex gap-2 mb-3">
            <input data-testid="si-create-name-input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t("si.name_ph", "e.g. Support")} className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button data-testid="si-create-btn" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4">
              {t("si.create", "Create")}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {members.map((m) => (
              <label key={m.id} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input data-testid={`si-create-member-${m.id}`} type="checkbox" checked={selMembers.includes(m.id)}
                  onChange={() => toggle(selMembers, setSelMembers, m.id)} className="w-4 h-4 accent-blue-600" />
                {m.name || m.email}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{t("si.connect_later", "You'll connect its Gmail account right after creating it.")}</p>
        </form>
      ) : (
        <p data-testid="si-limit-hint" className="text-xs text-amber-600">
          {limit === 0
            ? t("si.not_in_plan", "Your plan does not include shared inboxes — upgrade to unlock them.")
            : t("si.limit_reached", "Shared inbox limit reached ({used} of {limit}) — upgrade your plan for more.", { used, limit })}
        </p>
      ))}
    </div>
  );
};
